import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

export function SecurityPage() {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>Security & Sandbox Rules</TypographyH2>
        <TypographyP>
          Goldypaper's theme system enforces strict security rules to protect couple data and published invitations. Understanding these rules helps you write themes that conform to the platform's sandbox.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Couple content is HTML-escaped by default</TypographyH3>
        <TypographyP>
          All couple-entered content (names, dates, messages, story text) is HTML-escaped by default before rendering. This prevents stored-XSS attacks — if a couple pastes HTML or JavaScript into a field, it renders as text, never as markup or executable code.
        </TypographyP>
        <TypographyP>
          Your template never reads couple content directly — it only places a block slot, and the
          platform renders that block's content server-side, already escaped, before it reaches the slot:
        </TypographyP>
        <CodeBlock
          code={`{% block "hero" %}`}
          language="liquid"
          className="my-4"
        />
        <TypographyP className="text-sm text-muted-foreground">
          If the couple's <TypographyInlineCode>names</TypographyInlineCode> field contained <TypographyInlineCode>&lt;script&gt;</TypographyInlineCode>, the platform's block renderer escapes it before the hero block's HTML is composed — it renders as the literal text <TypographyInlineCode>&lt;script&gt;</TypographyInlineCode>, never executed.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>No escape hatch — designers cannot bypass escaping</TypographyH3>
        <TypographyP>
          There is no way for a theme designer to output couple content unescaped. Templates never get a <TypographyInlineCode>content</TypographyInlineCode> variable or any other reference to raw couple content — only the <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> tag, which always renders the platform's already-escaped output. This is intentional: it closes the security gap between "escaped by default" and a designer accidentally or intentionally defeating it.
        </TypographyP>
        <TypographyP>
          This is not a limitation — it is a feature that makes the platform safe for user-generated content.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>richtext: the only HTML-emitting field type</TypographyH3>
        <TypographyP>
          <TypographyInlineCode>richtext</TypographyInlineCode> is the only field type that permits HTML output. Story text and other long-form content can use it to support basic formatting (bold, italic, links, lists).
        </TypographyP>
        <TypographyP>
          <TypographyInlineCode>richtext</TypographyInlineCode> output is sanitized server-side with an allowlist of safe HTML tags and attributes:
        </TypographyP>
        <TypographyList>
          <li>Tags: <TypographyInlineCode>p</TypographyInlineCode>, <TypographyInlineCode>br</TypographyInlineCode>, <TypographyInlineCode>strong</TypographyInlineCode>, <TypographyInlineCode>em</TypographyInlineCode>, <TypographyInlineCode>b</TypographyInlineCode>, <TypographyInlineCode>i</TypographyInlineCode>, <TypographyInlineCode>u</TypographyInlineCode>, <TypographyInlineCode>a</TypographyInlineCode>, <TypographyInlineCode>ul</TypographyInlineCode>, <TypographyInlineCode>ol</TypographyInlineCode>, <TypographyInlineCode>li</TypographyInlineCode>, <TypographyInlineCode>h1</TypographyInlineCode>, <TypographyInlineCode>h2</TypographyInlineCode>, <TypographyInlineCode>h3</TypographyInlineCode>, <TypographyInlineCode>blockquote</TypographyInlineCode></li>
          <li>Attributes: <TypographyInlineCode>href</TypographyInlineCode>, <TypographyInlineCode>title</TypographyInlineCode>, <TypographyInlineCode>style</TypographyInlineCode> (CSS-scrubbed to safe properties only, e.g. <TypographyInlineCode>text-align</TypographyInlineCode>)</li>
        </TypographyList>
        <TypographyP>
          Any tag or attribute outside this allowlist is stripped, and any unsafe CSS property or value inside a <TypographyInlineCode>style</TypographyInlineCode> attribute is scrubbed. This is enforced by the platform render engine, not by your template.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Liquid sandbox — allowed tags and no filters</TypographyH3>
        <TypographyP>
          Your template runs in a sandboxed Liquid environment. Only a small set of custom tags are available:
        </TypographyP>
        <TypographyList>
          <li><TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> — render a platform block instance</li>
          <li><TypographyInlineCode>{`{% asset_url 'path' %}`}</TypographyInlineCode> — resolve a theme asset URL</li>
          <li><TypographyInlineCode>{`{% platform_head %}`}</TypographyInlineCode> — inject platform scripts and a manifest-declared trusted theme runtime</li>
        </TypographyList>
        <TypographyP>
          Filters (the <TypographyInlineCode>|</TypographyInlineCode> syntax in Liquid) are not allowed. Attempting to use a filter raises an error during render. This limitation is intentional: filters add surface area for introducing unsafe transformations or bypasses.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Trusted JavaScript for curated themes</TypographyH3>
        <TypographyP>
          A contract v3 theme may declare one optional JavaScript runtime in <TypographyInlineCode>theme.json</TypographyInlineCode>. Goldypaper loads that entrypoint after its allowlisted dependencies. Theme templates still cannot include <TypographyInlineCode>&lt;script&gt;</TypographyInlineCode> tags or event handlers like <TypographyInlineCode>onclick</TypographyInlineCode>; the manifest is the single auditable loading path.
        </TypographyP>
        <TypographyP>
          Runtime code is trusted application code, not sandboxed code. Every theme is curated and its JavaScript receives the same code review and testing as platform JavaScript. Dependencies use platform-owned IDs such as <TypographyInlineCode>jquery-3</TypographyInlineCode>; arbitrary package names, URLs, CDN scripts, and remote imports are not supported.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Strict Content-Security-Policy on published pages</TypographyH3>
        <TypographyP>
          Every published invitation is served with a strict Content-Security-Policy (CSP) header that prevents:
        </TypographyP>
        <TypographyList>
          <li>Inline scripts — even a <TypographyInlineCode>&lt;script&gt;</TypographyInlineCode> tag in the HTML is blocked</li>
          <li>Off-origin scripts — platform, dependency, and theme runtime scripts must all be served by Goldypaper</li>
          <li>Inline styles — <TypographyInlineCode>style=""</TypographyInlineCode> attributes are blocked</li>
          <li>External stylesheets — CSS can only be loaded from the platform's own domain</li>
        </TypographyList>
        <TypographyP>
          The CSP header is set at render time by the platform, not something you control in your template. It acts as a defense-in-depth layer: even if an XSS payload somehow bypassed other safeguards, the browser refuses to execute it.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Defense in depth</TypographyH3>
        <TypographyP>
          Goldypaper's security model layers four independent protections:
        </TypographyP>
        <TypographyList>
          <li><strong>Sanitizer:</strong> Couple content is escaped server-side; richtext output is allowlist-sanitized</li>
          <li><strong>Sandboxed Liquid:</strong> Templates can only use safe tags; no filters, no file access, no system access</li>
          <li><strong>Curated runtime:</strong> one manifest-declared entrypoint may use only platform-allowlisted dependencies</li>
          <li><strong>Strict CSP:</strong> the browser blocks inline and off-origin scripts regardless of template content</li>
        </TypographyList>
        <TypographyP>
          These layers protect user-generated couple content while keeping theme execution explicit. Curation and code review are the trust boundary for runtime code; CSP is defense in depth, not a JavaScript sandbox.
        </TypographyP>
      </div>
    </div>
  )
}
